<template>
  <div>
    <div class="share-bar">
      <template v-if="inWeChat">
        <b-button size="sm" :variant="iconOnly ? 'outline-secondary' : 'success'" :class="{'px-2': iconOnly, 'shadow-sm': iconOnly}" @click="showWeChatShareGuide" :title="iconOnly ? '分享到微信' : ''">
          <b-icon icon="share" v-if="iconOnly"></b-icon>
          <span v-else>分享到微信</span>
        </b-button>
        <span class="share-tip" v-if="shareGuideVisible">已设置分享内容，请点击右上角 ··· 菜单进行分享</span>
      </template>
      <template v-else-if="isMobile">
        <b-button size="sm" :variant="iconOnly ? 'outline-secondary' : 'success'" :class="{'px-2': iconOnly, 'shadow-sm': iconOnly}" @click="handleMobileShare" :title="iconOnly ? '分享给好友' : ''">
          <b-icon icon="share" v-if="iconOnly"></b-icon>
          <span v-else>分享给好友</span>
        </b-button>
      </template>
      <template v-else>
        <b-button size="sm" :variant="iconOnly ? 'outline-secondary' : 'success'" :class="{'px-2': iconOnly, 'shadow-sm': iconOnly}" @click="showPcQrShare" :title="iconOnly ? '微信分享' : ''">
          <b-icon icon="share" v-if="iconOnly"></b-icon>
          <span v-else>微信分享</span>
        </b-button>
      </template>
    </div>

    <!-- PC端微信分享二维码弹窗 -->
    <b-modal v-model="showQrModal" title="微信分享" hide-footer centered body-class="p-0">
      <div class="share-modal-content">
        <div class="share-card-preview">
          <div class="share-card-left">
            <div class="share-card-title">{{ title }}</div>
            <div class="share-card-desc">{{ desc }}</div>
          </div>
          <div class="share-card-right">
            <img :src="shareImg" class="share-card-thumb" alt="share-thumb">
          </div>
        </div>
        
        <div class="qr-section text-center mt-4 mb-3">
          <canvas ref="qrCanvas"></canvas>
          <p class="mt-2 text-muted small">打开微信，使用"扫一扫"即可分享到微信</p>
        </div>

        <!-- 链接复制与操作区 -->
        <hr class="my-3" />
        <div class="share-link-box p-3 bg-light rounded border text-left mb-3">
          <div class="small text-muted mb-1">分享链接:</div>
          <div class="d-flex align-items-center justify-content-between">
            <div class="text-truncate text-primary small flex-grow-1 mr-2" style="max-width: 70%;">{{ shareLink }}</div>
            <b-button variant="outline-primary" size="sm" class="flex-shrink-0" @click="copyShareLink">
              复制链接
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { request_zzquant } from '@/network/request'
import QRCode from 'qrcode'
import logo from '@/assets/img/logo.png'

export default {
  name: 'WxShare',
  props: {
    iconOnly: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '自在量化 · 市场热度与量化分析'
    },
    desc: {
      type: String,
      default: '市场题材趋势量化、情绪量化与市场洞察，一起量化市场的一切'
    },
    link: {
      type: String,
      default: ''
    },
    imgUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      inWeChat: false,
      shareGuideVisible: false,
      showQrModal: false
    }
  },
  computed: {
    inviteCode() {
      let code = localStorage.getItem('my_invite_code')
      if (!code) {
        try {
          const info = JSON.parse(localStorage.getItem('user_info'))
          if (info && info.invite_code) {
            code = info.invite_code
          }
        } catch (e) { /* ignore */ }
      }
      return code || ''
    },
    shareLink() {
      const baseLink = this.link || window.location.href
      const code = this.inviteCode
      
      if (code) {
        return this.appendInviteCode(baseLink, code)
      }
      
      // If not logged in, try to preserve the existing invite code from the baseLink or current page URL
      if (baseLink.includes('invite_code=')) {
        return baseLink
      }
      
      try {
        const urlParams = new URLSearchParams(window.location.search)
        const currentCode = urlParams.get('invite_code')
        if (currentCode) {
          return this.appendInviteCode(baseLink, currentCode)
        }
      } catch (e) { /* ignore */ }
      
      return baseLink
    },
    shareImg() {
      let img = this.imgUrl || logo
      if (typeof img === 'string') {
        if (img.startsWith('//')) return window.location.protocol + img
        if (img.startsWith('/')) return window.location.origin + img
      }
      return img
    },
    isMobile() {
      try {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '')
      } catch (e) {
        return false
      }
    }
  },
  watch: {
    title() { this.updateWeChatShare() },
    desc() { this.updateWeChatShare() },
    link() { this.updateWeChatShare() },
    imgUrl() { this.updateWeChatShare() }
  },
  mounted() {
    this.inWeChat = this.isWeChatUA()
    if (this.inWeChat) {
      this.loadScript('https://res.wx.qq.com/open/js/jweixin-1.6.0.js')
        .then(() => this.initWeChatSDK())
        .catch((e) => { console.error('Load WX SDK failed', e) })
    }
  },
  methods: {
    appendInviteCode(url, code) {
      if (!url || !code) return url
      try {
        if (url.startsWith('http://') || url.startsWith('https://')) {
          const parsedUrl = new URL(url)
          parsedUrl.searchParams.set('invite_code', code)
          return parsedUrl.toString()
        } else {
          // Relative url or hash-based path
          if (url.includes('invite_code=')) {
            return url.replace(/invite_code=[^&]+/, `invite_code=${code}`)
          }
          const separator = url.includes('?') ? '&' : '?'
          return `${url}${separator}invite_code=${code}`
        }
      } catch (e) {
        if (url.includes('invite_code=')) {
          return url.replace(/invite_code=[^&]+/, `invite_code=${code}`)
        }
        const separator = url.includes('?') ? '&' : '?'
        return `${url}${separator}invite_code=${code}`
      }
    },
    isWeChatUA() {
      try {
        return /MicroMessenger/i.test(navigator.userAgent || '')
      } catch (e) {
        return false
      }
    },
    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (window.wx || window.jWeixin) {
          resolve()
          return
        }
        const s = document.createElement('script')
        s.src = src
        s.onload = resolve
        s.onerror = reject
        document.head.appendChild(s)
      })
    },
    initWeChatSDK() {
      const url = location.href.split('#')[0]
      return request_zzquant({
        url: '/v2/login/wx/js_sign',
        method: 'GET',
        params: { url }
      }).then(res => {
        const d = res && res.data && res.data.data ? res.data.data : null
        if (!d) return
        const wx = window.wx || window.jWeixin
        if (!wx) return
        wx.config({
          debug: false,
          appId: d.appId,
          timestamp: d.timestamp,
          nonceStr: d.nonceStr,
          signature: d.signature,
          jsApiList: [
            'updateAppMessageShareData',
            'updateTimelineShareData',
            'onMenuShareAppMessage',
            'onMenuShareTimeline'
          ]
        })
        wx.ready(() => {
          this.updateWeChatShare()
        })
        wx.error((res) => {
          console.error('WX SDK Config Error:', res)
        })
      }).catch(err => {
        console.error('WX Sign Error:', err)
      })
    },
    updateWeChatShare() {
      const wx = window.wx || window.jWeixin
      if (!wx) return
      const data = {
        title: this.title,
        desc: this.desc,
        link: this.shareLink,
        imgUrl: this.shareImg
      }
      
      // New APIs
      try { wx.updateAppMessageShareData(data) } catch(e) { console.warn('updateAppMessageShareData failed', e) }
      try { wx.updateTimelineShareData({ title: data.title, link: data.link, imgUrl: data.imgUrl }) } catch(e) { console.warn('updateTimelineShareData failed', e) }
      
      // Fallback for older versions
      try { wx.onMenuShareAppMessage(data) } catch(e) { /* ignore */ }
      try { wx.onMenuShareTimeline({ title: data.title, link: data.link, imgUrl: data.imgUrl }) } catch(e) { /* ignore */ }
    },
    showWeChatShareGuide() {
      this.shareGuideVisible = true
      setTimeout(() => { this.shareGuideVisible = false }, 4000)
      this.updateWeChatShare()
    },
    handleMobileShare() {
      if (navigator.share) {
        navigator.share({
          title: this.title,
          text: this.desc,
          url: this.shareLink
        }).catch(err => {
          console.warn('Native share cancelled or failed:', err)
          this.copyLinkFallback()
        })
      } else {
        this.copyLinkFallback()
      }
    },
    copyLinkFallback() {
      const copyText = `${this.desc} ${this.shareLink}`
      navigator.clipboard.writeText(copyText).then(() => {
        if (this.$bvToast) {
          this.$bvToast.toast('分享内容及链接已复制到剪贴板，快去发给微信好友/朋友圈吧！', {
            title: '复制成功',
            variant: 'success',
            solid: true,
            appendToast: true,
            toaster: 'b-toaster-top-center',
            autoHideDelay: 3000
          })
        } else {
          alert('分享内容及链接已复制到剪贴板，快去发给微信好友吧！')
        }
      }).catch(err => {
        console.error('Copy link failed:', err)
        if (this.$bvToast) {
          this.$bvToast.toast('复制失败，请手动复制浏览器地址栏链接。', {
            title: '复制失败',
            variant: 'danger',
            solid: true,
            appendToast: true,
            toaster: 'b-toaster-top-center',
            autoHideDelay: 3000
          })
        }
      })
    },
    showPcQrShare() {
      this.showQrModal = true
      this.$nextTick(() => {
        const canvas = this.$refs.qrCanvas
        const url = this.shareLink
        QRCode.toCanvas(canvas, url, { width: 180, margin: 1 }, (error) => {
          if (error) console.error(error)
        })
      })
    },
    copyShareLink() {
      navigator.clipboard.writeText(this.shareLink).then(() => {
        if (this.$bvToast) {
          this.$bvToast.toast('分享链接已成功复制到剪贴板！', {
            title: '复制成功',
            variant: 'success',
            solid: true,
            appendToast: true,
            toaster: 'b-toaster-top-center',
            autoHideDelay: 2000
          })
        } else {
          alert('分享链接已复制！')
        }
      }).catch(err => {
        console.error('Copy link failed:', err)
        if (this.$bvToast) {
          this.$bvToast.toast('复制失败，请手动复制链接。', {
            title: '复制失败',
            variant: 'danger',
            solid: true,
            appendToast: true,
            toaster: 'b-toaster-top-center',
            autoHideDelay: 2000
          })
        }
      })
    }
  }
}
</script>

<style scoped>
.share-bar {
  display: flex;
  align-items: center;
  padding: 0px;
  background: transparent;
}
.share-bar .share-tip {
  margin-left: 5px;
  color: #888;
  font-size: 12px;
}

/* Modal Styles */
.share-modal-content {
  padding: 20px;
}

.share-card-preview {
  display: flex;
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  margin-bottom: 20px;
}

.share-card-left {
  flex: 1;
  padding-right: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.share-card-title {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin-bottom: 4px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.share-card-desc {
  font-size: 12px;
  color: #888;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.share-card-right {
  width: 50px;
  height: 50px;
  flex-shrink: 0;
}

.share-card-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 2px;
}

.qr-section canvas {
  width: 180px !important;
  height: 180px !important;
}

.share-link-box {
  margin-top: 15px;
}

/* 暗黑模式 */
body.theme-dark .share-link-box {
  background-color: #1f242c !important;
  border-color: #30363d !important;
}
</style>
